<template>
    <div class="button-group-container">
        <label v-if="label" class="button-group-label">{{ label }}</label>
        <div class="button-group" :class="{ 'has-input': showInput }">
            <button
v-for="(option, index) in options" :key="option.id" :class="[
                'button',
                {
                    'active': value === option.id,
                    'first-button': index === 0,
                    'last-button': index === options.length - 1
                }
            ]" @click="toggle(option.id)">
                {{ option.label }}
            </button>
        </div>
        <input
v-if="showInput" v-model="inputValue" type="text" class="toggle-input form-control"
            :class="{ show: showInput }" placeholder="Ingrese un valor" @input="emitInputValue" />
    </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, watch, onMounted } from 'vue';

const props = defineProps({
    modelValue: {
        type: Number,
        default: null
    },
    textValue: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    options: {
        type: Array,
        default: () => []
    },
    inputCondition: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue', 'update:textValue']);

const value = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val)
});

const inputValue = ref(props.textValue);

const showInput = ref(false);

const toggle = (val) => {
    value.value = val;
    showInput.value = props.inputCondition.includes(val);
    if (!showInput.value) {
        inputValue.value = ''; // Reset input value if input is hidden
    }
};

const emitInputValue = () => {
    emit('update:textValue', inputValue.value);
};

watch(value, (newValue) => {
    showInput.value = props.inputCondition.includes(newValue);
    if (showInput.value) {
        inputValue.value = props.textValue; // Update inputValue when showInput is true
    }
});

onMounted(() => {
    showInput.value = props.inputCondition.includes(value.value);
    if (showInput.value) {
        inputValue.value = props.textValue; // Initialize inputValue when component mounts
    }
});
</script>

<style scoped>
.button-group-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
}

.button-group-label {
    font-weight: 700;
    font-size: 0.9rem;
    color: #1e293b;
    margin-bottom: 0.5rem;
    display: block;
}

.button-group {
    display: flex;
    justify-content: center;
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    transition: border-radius 0.3s ease;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
}

/* Si hay un input visible, solo las esquinas superiores son redondeadas */
.button-group.has-input {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom: none;
}

.button {
    padding: 10px 0;
    border: none;
    background-color: #f8fafc;
    color: #64748b;
    cursor: pointer;
    transition: all 0.3s ease;
    flex: 1;
    font-size: 0.875rem;
    font-weight: 600;
    position: relative;
}

.button:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 25%;
    height: 50%;
    width: 1px;
    background-color: #cbd5e1;
    transition: opacity 0.3s;
}

.button.active::after {
    opacity: 0;
}

/* Hide separator of the previous button if the current one is active */
.button.active+.button::after {
    opacity: 0;
    /* This doesn't work for the previous element, but z-index handles the active one covering its own right border if it had one. The separator is on the right of the element. */
}

.button.active {
    background-image: linear-gradient(310deg, #5e72e4, #825ee4);
    color: white;
    font-weight: 700;
    z-index: 1;
}

.button:hover:not(.active) {
    background-color: #f1f5f9;
    color: #334155;
}

/* Estilos y animación para el input */
.toggle-input {
    opacity: 0;
    max-height: 0;
    transition: opacity 0.3s ease, max-height 0.3s ease;
    overflow: hidden;
}

/* Cuando el input está visible */
.toggle-input.show {
    opacity: 1;
    max-height: 100px;
    /* Ajusta según la altura deseada */
    border-radius: 0 0 10px 10px;
    /* Borde redondeado en la parte inferior */
}
</style>
